import { CustomImageType } from "@bared/groq";
import { useLocalisation } from "@hooks/useLocalisation";
import { useRoutes } from "@hooks/useRoutes";
import {
  ComponentProps,
  FromSanityGraphQL,
  ResolvedLink,
} from "@ts/components";
import { Content, SectionCollectionHeroBanner } from "@ts/sanity";
import { ConfigFilterOption, useFilters } from "@usereactify/search";
import React, { FC, useEffect, useState } from "react";

type CollectionHeroBannerSizeFiltersInputProps = ComponentProps &
  FromSanityGraphQL<SectionCollectionHeroBanner> & {
    desktopBanner?: Content;
    mobileBanner?: Content;
    collectionTitle: string;
    desktopImage?: CustomImageType;
    mobileImage?: CustomImageType;
  };

type CollectionHeroBannerSizeFiltersOutputProps = Omit<
  CollectionHeroBannerSizeFiltersInputProps,
  "faqLink"
> & {
  active: boolean;
  categoryFilter?: ConfigFilterOption;
  handleViewAllClick: () => void;
  handleCategoryClick: () => void;
  link?: ResolvedLink;
  sizeFilter?: ConfigFilterOption;
};

export const withCollectionHeroBannerSizeFilters =
  (Component: FC<CollectionHeroBannerSizeFiltersOutputProps>) =>
  ({
    name = "CollectionHeroBannerSizeFilters",
    collectionTitle,
    desktopBanner,
    mobileBanner,
    location,
    ...props
  }: CollectionHeroBannerSizeFiltersInputProps) => {
    // This component needs to be inside a Reactify provider to work properly
    // calling useFilters will trigger an exception because it tries to access the
    // Reactify context, which doesn't exist
    try {
      const { linkResolver } = useRoutes();
      const { filters } = useFilters();
      const { navigate } = useLocalisation();

      const [active, setActive] = useState(true);
      const [size, setSize] = useState<ConfigFilterOption | undefined>();
      const [category, setCategory] = useState<
        ConfigFilterOption | undefined
      >();

      const sizeFilter = filters?.find((filter) => filter.handle === "size");
      const discountFilter = filters?.find(
        (filter) => filter.handle === "discount",
      );
      const link = linkResolver(props.faqLink);
      const route = window.location.pathname;

      const handleCategoryClick = () => {
        setActive(false);
      };

      const handleViewAllClick = () => {
        navigate(route);
        setActive(true);
      };

      useEffect(() => {
        if (!!window.location?.search.includes("category")) {
          setActive(false);
        } else {
          setActive(true);
        }
      }, [window.location?.search]);

      useEffect(() => {
        setSize(sizeFilter);
        setCategory(discountFilter);
      }, [filters]);

      return (
        <Component
          active={active}
          categoryFilter={category}
          collectionTitle={collectionTitle}
          desktopBanner={desktopBanner}
          handleCategoryClick={handleCategoryClick}
          handleViewAllClick={handleViewAllClick}
          link={link}
          mobileBanner={mobileBanner}
          sizeFilter={size}
          {...props}
        />
      );
    } catch (err) {
      console.error(err);
      return null;
    }
  };
